import { useState } from 'react'
import { REACT_SELECT_DEFAULT_CONTROL_HEIGHT } from 'components/FormUtils/ReactSelectWrapper'
import Checkbox from 'components/Checkbox/checkbox'
import { Kpi, saveKpi, useKpis } from '../kpis'
import { FORM_INPUT_CLASS } from './formStyles'
import { MatchedSelect } from './MatchedSelect'
import { ViewConfig } from '../view-config'

/**
 * Goal editor for an aggregate report. The goal has two sources:
 *
 *   - **Enter value** — a number typed here, saved with the report. Name it and
 *     hit "Save as reusable goal" to promote it to a shared, reusable goal and
 *     link the report to it (naming is optional — leave it unsaved to keep a
 *     plain one-off goal).
 *   - **Saved goal** — points at a reusable saved goal (`kpis.ts`) by `kpiId`.
 *     The value is then sourced from the live goal (via `resolveThreshold` at
 *     render), so one goal can back several reports/plots and editing it moves
 *     them all at once. (The store entity is still named `Kpi` internally.)
 *
 * Either way it writes `config.threshold`, drawn as a reference line on charts
 * (ReportChart) and a met/missed marker on the Number view (MetricView). Shared
 * by both the flat report editor and the pipeline builder so both behave the
 * same.
 *
 * @param config - the report being edited
 * @param onChange - receives the next config with `threshold` set/cleared
 */
const SOURCE_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'manual', label: 'Enter value' },
  { value: 'linked', label: 'Saved goal' },
]

export function ThresholdControl({
  config,
  onChange,
}: {
  config: ViewConfig
  onChange: (next: ViewConfig) => void
}) {
  const { kpis, bump } = useKpis()
  const threshold = config.threshold
  const dir = threshold?.dir ?? 'above'
  const linked = !!threshold?.kpiId
  const linkedKpi = kpis.find((k) => k.id === threshold?.kpiId)
  // Name to attach when promoting the current manual value via "Save as KPI".
  const [saveName, setSaveName] = useState('')

  // Goal source is three-way: None (no goal), Enter value, or a Saved goal.
  // Tracked locally so an empty "Enter value" (threshold cleared while typing)
  // stays distinct from an explicit "None". Seeded once from the config — the
  // route remounts this per report (key={viewId}), so the seed stays correct.
  const [source, setSource] = useState<'none' | 'manual' | 'linked'>(
    linked ? 'linked' : threshold ? 'manual' : 'none'
  )

  // Switch source. None → clear the goal; Enter value → keep the number, drop any
  // link; Saved goal → link the current/first saved goal.
  const changeSource = (next: 'none' | 'manual' | 'linked') => {
    setSource(next)
    if (next === 'none') return onChange({ ...config, threshold: undefined })
    if (next === 'manual') {
      if (!threshold) return // input creates the threshold on first keystroke
      const { kpiId: _kpiId, ...rest } = threshold
      return onChange({ ...config, threshold: rest })
    }
    const kpi = linkedKpi ?? kpis[0]
    if (kpi) linkTo(kpi) // no-op when there's no saved goal to link yet
  }

  // Point the target at a KPI: seed value/dir from it but keep any explicit
  // dir/perSplit already set on the threshold.
  const linkTo = (kpi: Kpi) =>
    onChange({
      ...config,
      threshold: {
        value: kpi.value,
        dir: threshold?.dir ?? kpi.dir ?? 'above',
        perSplit: threshold?.perSplit,
        kpiId: kpi.id,
      },
    })

  // Promote the current manual value to a reusable saved goal and link to it.
  const saveAsKpi = () => {
    if (threshold?.value === undefined) return
    const kpi = saveKpi({
      name: saveName.trim() || 'Untitled goal',
      value: threshold.value,
      dir,
    })
    bump()
    setSaveName('')
    onChange({ ...config, threshold: { ...threshold, kpiId: kpi.id } })
  }

  // Above/Below picker — shared by both sources. "Above" means the value is good
  // when at or over the goal (≥), "Below" when at or under (≤); the boundary is
  // implied. Direction only persists once there's a threshold to attach it to.
  const directionSelect = (
    <MatchedSelect
      ariaLabel="Goal direction"
      options={[
        { value: 'above', label: 'Above' },
        { value: 'below', label: 'Below' },
      ]}
      value={{
        value: dir,
        label: dir === 'above' ? 'Above' : 'Below',
      }}
      onChange={(d) =>
        threshold &&
        onChange({
          ...config,
          threshold: { ...threshold, dir: d as 'above' | 'below' },
        })
      }
    />
  )

  return (
    <div className="flex flex-col gap-2">
      <div className="mb-1 text-xs text-fg-secondary">Goal</div>

      {/* Source: no goal, a plain number, or a saved KPI shared across reports. */}
      <MatchedSelect
        ariaLabel="Goal source"
        options={SOURCE_OPTIONS}
        value={SOURCE_OPTIONS.find((o) => o.value === source)}
        onChange={(s) => changeSource(s as 'none' | 'manual' | 'linked')}
      />

      {source === 'linked' ? (
        <>
          <MatchedSelect
            ariaLabel="Saved goal"
            options={kpis.map((k) => ({ value: k.id, label: k.name }))}
            value={
              linkedKpi
                ? { value: linkedKpi.id, label: linkedKpi.name }
                : undefined
            }
            onChange={(id) => {
              const kpi = kpis.find((k) => k.id === id)
              if (kpi) linkTo(kpi)
            }}
          />
          <div className="text-xs text-fg-secondary">
            Value{' '}
            <span className="font-semibold text-fg">{linkedKpi?.value}</span> —
            sourced from the goal; editing the goal updates every linked report.
          </div>
          {directionSelect}
        </>
      ) : source === 'manual' ? (
        <>
          <div className="flex items-center gap-2">
            {directionSelect}
            <input
              type="number"
              inputMode="numeric"
              aria-label="Goal value"
              placeholder="optional"
              value={threshold?.value ?? ''}
              style={{ height: REACT_SELECT_DEFAULT_CONTROL_HEIGHT }}
              onChange={(e) => {
                const raw = e.target.value
                if (raw === '')
                  return onChange({ ...config, threshold: undefined })
                const value = Number(raw)
                if (Number.isNaN(value)) return
                onChange({ ...config, threshold: { value, dir } })
              }}
              className="w-28 rounded-md border border-line bg-surface px-3 text-sm text-fg outline-none focus:border-brand-active"
            />
          </div>
          {/* Name it and save to reuse it elsewhere — or leave it as a one-off. */}
          {threshold?.value !== undefined && (
            <div className="flex flex-col gap-2">
              <input
                type="text"
                aria-label="Goal name"
                placeholder="Name this goal"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                className={FORM_INPUT_CLASS}
              />
              <button
                type="button"
                onClick={saveAsKpi}
                className="self-start rounded-md border border-line px-3 py-1.5 text-sm text-fg hover:bg-surface-hover"
              >
                Save as reusable goal
              </button>
            </div>
          )}
        </>
      ) : null}

      {/* On a split chart the target is the team total by default; opt into a
          per-series (per-rep) target to compare each split against the value. */}
      {threshold && config.seriesBy && (
        <label className="flex cursor-pointer items-center gap-2 text-sm text-fg">
          <Checkbox
            boxSize="sm"
            checked={!!threshold.perSplit}
            onChange={() =>
              onChange({
                ...config,
                threshold: { ...threshold, perSplit: !threshold.perSplit },
              })
            }
          />
          Target is per-split
        </label>
      )}
    </div>
  )
}
